import React, {
	useCallback,
	useState,
	useEffect,
	useRef,
	useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/styles';
import { ArrowClockwise } from 'react-bootstrap-icons';
import Box from '@mui/material/Box';
import { TABLE_SETTING } from '../../constants/Tables';
import { useUserEmail } from '../../authentication/useAuth';
import { useSessionState } from '../../hooks/useSessionState';
import usePersistentTableColumns from '../../hooks/usePersistentTableColumns';
import ProductsTable from '../../components/ProductsTable';
import ProductsFilters from '../../components/ProductsTable/components/ProductFilters';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import {
	makeProductsColumnConfig,
	columnOrder as defaultColumnOrder,
	columnVisibility as defaultColumnVisibility,
} from '../../components/ProductsTable/columnConfig';
import ClearButton from '../../components/ClearButton';
import Error from '../../components/Error';
import DataExport from '../../components/ProductsTable/components/DataExport';
import { useProductsApi } from '../../apis';
import { downloadFileFromApi } from '../../apis/Utilities/downloadFileFromApi';

function Products() {
	const theme = useTheme();
	const { t } = useTranslation('common');
	const isMountedRef = useIsMountedRef();
	const [products, setProducts] = useState(null);
	const [filters, setFilters] = useSessionState('products_filters', {});
	const [sorts, setSorts] = useSessionState('products_sorts', []);
	const [isLoading, setIsLoading] = useState(false);
	const [printCardDownloadError, setPrintCardDownloadError] = useState('');
	const openFiltersRef = useRef(null);
	const { downloadPrintCard, getProductList } = useProductsApi();

	const [{ limit, page }, setPageParams] = useSessionState(
		'products_page_params',
		{
			limit: TABLE_SETTING.DEFAULT_PAGE_SIZE,
			page: 0,
		}
	);

	const { columnOrder, columnVisibility, setColumnVisibility, resetColumns } =
		usePersistentTableColumns({
			storageKeyPrefix: 'products',
			defaultColumnOrder,
			defaultColumnVisibility,
		});

	useEffect(() => {
		const isVisible = (key) => Object.keys(columnVisibility).includes(key);

		if (sorts.length && !sorts.every(({ key }) => isVisible(key))) {
			// if it's not in the columnVisibility it should be removed from sorts
			setSorts(sorts.filter(({ key }) => isVisible(key)));
		}
	}, [sorts, columnVisibility, setSorts]);

	const sortingKeys = {
		Customer_Item_Number: 'Customer_Item_Number',
		Design_Number: 'Design_Number',
		Item_Number: 'Item_Number',
		Item_Description: 'Item_Description',
		Last_Ordered_Date: 'Last_Ordered_Date',
		Order_Count: 'Order_Count',
	};

	const sort = sorts
		.map(
			({ key, direction }) =>
				`${sortingKeys[key] || key} ${direction.toUpperCase()}`
		)
		.join(', ');

	const handlePrintCardDownload = useCallback(
		async ({ productId }) => {
			if (!productId) {
				return;
			}

			setPrintCardDownloadError('');

			await downloadFileFromApi({
				format: 'pdf',
				defaultFilenamePrefix: `PrintCard_${productId}`,
				executeApiCall: () => downloadPrintCard(productId),
				onError: () => {
					setPrintCardDownloadError(t('downloadError'));
				},
			});
		},
		[downloadPrintCard, t]
	);

	const productsColumnConfig = useMemo(
		() =>
			makeProductsColumnConfig({
				onPrintCardDownload: handlePrintCardDownload,
				downloadLabel: t('download'),
			}),
		[handlePrintCardDownload, t]
	);

	const fetchProducts = useCallback(async () => {
		setIsLoading(true);

		const response = await getProductList({
			limit,
			page,
			filters,
			UserID: useUserEmail.email,
			Order_By: sort || 'Design_Number ASC',
		});

		if (response?.isAborted) {
			setIsLoading(false);
			return;
		}

		if (response?.isError) {
			console.log('err', response.error || 'failed to load products');
			setIsLoading(false);
			return;
		}

		if (isMountedRef.current) {
			setProducts(response?.data || null);
		}

		setIsLoading(false);
	}, [isMountedRef, filters, limit, page, sort, getProductList]);

	useEffect(() => {
		fetchProducts();
	}, [fetchProducts]);

	return (
		<ProductsTable
			products={products?.Products || []}
			total={products?.Count || 0}
			sorts={sorts}
			onSort={setSorts}
			onPageChange={setPageParams}
			pagination={{ limit, page }}
			filters={filters}
			onFilter={setFilters}
			columnOrder={columnOrder}
			columnVisibility={columnVisibility}
			onColumnVisibilityChange={setColumnVisibility}
			columnConfig={productsColumnConfig}
			isLoading={isLoading}
		>
			<Box
				inputpros={{
					'aria-label': 'Error and Success messages',
					role: 'row',
				}}
				component="div"
				sx={{
					display: { xs: 'none', sm: 'block' },
					width: '100%',
					marginLeft: '-.65rem',
				}}
			>
				{!!printCardDownloadError && (
					<Error message={printCardDownloadError} fullWidth />
				)}
			</Box>
			{!!sorts.length > 0 && (
				<Box
					inputpros={{
						'aria-label': 'order filters',
						role: 'row',
					}}
					component="div"
					sx={{
						display: 'flex',
						alignItems: 'right',
						justifyContent: 'flex-end',
						margin: {
							xs: '.5rem .2rem auto -.2rem',
							sm: '.1rem .65rem .5rem .5rem',
						},
					}}
				>
					<ClearButton
						onChange={setSorts}
						label="clear_sort"
						icon={
							<ArrowClockwise
								style={{
									fontSize: theme.palette.textSize.sizeXs,
									color: theme.palette.link.main,
								}}
							/>
						}
					/>
				</Box>
			)}
			<Box
				inputpros={{
					'aria-label': 'order filters',
					role: 'row',
				}}
				component="div"
				sx={{
					display: 'inline',
					width: '6rem',
					margin: {
						xs: '.5rem .2rem auto -.2rem',
						sm: '.1rem .65rem .5rem .5rem',
					},
				}}
			>
				<ProductsFilters
					onFilter={(_filters) => {
						setFilters(_filters);
						setPageParams({ page: 0, limit });
					}}
					value={filters}
					dataTestId="products_table_filter"
					openRef={(ref) => {
						openFiltersRef.current = ref;
					}}
				/>
			</Box>
			<Box
				inputpros={{
					'aria-label': 'data export',
					role: 'row',
				}}
				component="div"
				sx={{
					display: 'inline',
					ml: '1.5rem',
					width: '9.25rem',
					minWidth: {
						xs: '90px',
						sm: '115px',
					},
				}}
			>
				<DataExport
					inputpros={{
						'aria-label': 'data export',
						role: 'cell',
					}}
					tabIndex={0}
					isLoading={isLoading}
					filters={filters}
					columnOrder={columnOrder}
					columnVisibility={columnVisibility}
					dataTestId="products_table_export"
					onVisibilityChange={setColumnVisibility}
					sort={sort}
					onReset={resetColumns}
				/>
			</Box>
		</ProductsTable>
	);
}

export default Products;
